import { cn } from '@chessmate/ui';
import { useT } from '../i18n/useT.js';
import type { GameEntry } from '../lib/games-catalog.js';

/** The picture area of a game: its cover, or a tinted gradient with a big symbol until one exists. */
export function GameCover({ game, className }: { game: GameEntry; className?: string }) {
  return (
    <div className={cn('relative overflow-hidden bg-gradient-to-br', game.tint, className)} data-cover={game.cover ? 'image' : 'fallback'}>
      {game.cover ? (
        <img src={game.cover} alt="" loading="lazy" className="size-full object-cover" />
      ) : (
        <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center text-6xl text-ink/60">
          {game.glyph}
        </span>
      )}
    </div>
  );
}

/**
 * One game on the Games page. A real button (keyboard and screen-reader friendly) named by the game
 * and its tagline; `selected` marks the game currently chosen. The testid is `game-<id>`, the same
 * id the old segmented picker used, so flows and specs keep addressing a game the same way.
 */
export function GameCard({ game, selected, onSelect }: { game: GameEntry; selected: boolean; onSelect: (id: GameEntry['id']) => void }) {
  const { t } = useT('games');
  const tagline = t(game.taglineKey);
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`${game.label}. ${tagline}`}
      data-testid={`game-${game.id}`}
      data-appdom-id={`game-${game.id}`}
      data-appdom-type="Button"
      onClick={() => onSelect(game.id)}
      className={cn(
        'group flex min-h-[48px] flex-col overflow-hidden rounded-3xl border bg-surface/90 text-left shadow-md backdrop-blur-sm transition-transform duration-150',
        'hover:-translate-y-1 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected ? 'border-accent ring-2 ring-accent' : 'border-border',
      )}
    >
      <GameCover game={game} className="aspect-[16/9] w-full" />
      <span className="flex flex-1 flex-col gap-2 p-4 lg:p-5">
        <span className="flex items-center gap-2">
          <span className="font-display text-xl font-bold text-fg lg:text-2xl">{game.label}</span>
          {game.isNew && (
            <span className="rounded-full bg-accent px-3 py-1 text-sm font-extrabold uppercase tracking-wide text-accent-fg">{t('games-new')}</span>
          )}
        </span>
        <span className="text-base font-semibold text-fg-muted">{tagline}</span>
        <span className="mt-auto flex flex-wrap gap-2 pt-1 text-sm font-bold text-fg-muted">
          <span className="rounded-full border border-border px-3 py-1">{t('games-chip-robot')}</span>
          {game.online && <span className="rounded-full border border-border px-3 py-1">{t('games-chip-online')}</span>}
        </span>
      </span>
    </button>
  );
}
